import { type ReactNode, useId } from "react";
import { ArrowRight } from "lucide-react";
import { buttonChrome } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import { Panel } from "@/components/ui/Panel";
import { MaybeLink } from "../../layout/MaybeLink";

/** Where a record is settled: the stage's name as the rail says it, and its route. */
export interface EditTarget {
  label: string;
  to: string;
}

/** The way back to the stage that settles a record, written as the design writes it. */
export function EditLink({ target }: { target: EditTarget }) {
  return (
    <MaybeLink to={target.to} className={buttonChrome({ variant: "link" })}>
      {`Edit in ${target.label}`}
      <ArrowRight aria-hidden />
    </MaybeLink>
  );
}

/**
 * One record the run reads: its mark and its name over a rule, the way back to
 * the stage that settles it at the far end, and the record as it stands. A
 * region named by its title, so a reader can move from card to card.
 */
export function ReviewCard({
  icon,
  title,
  edit,
  meta,
  children,
}: {
  icon: ReactNode;
  title: string;
  edit?: EditTarget;
  /** A few words at the far end in place of the way back, for a card with none. */
  meta?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <Panel as="section" pad="none" aria-labelledby={id} className="mt-4">
      <div className="flex items-center gap-3 border-b border-border-subtle px-5 py-3.5">
        <IconTile size="md">{icon}</IconTile>
        <h2 id={id} className="text-[15px] font-bold">
          {title}
        </h2>
        {edit !== undefined && (
          <span className="ml-auto">
            <EditLink target={edit} />
          </span>
        )}
        {meta !== undefined && (
          <span className="ml-auto text-[11px] text-subtle-foreground">
            {meta}
          </span>
        )}
      </div>
      <div className="px-5 pt-4 pb-5">{children}</div>
    </Panel>
  );
}
